/**
 * Category combobox (shared).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ChevronRight, ChevronsUpDown } from "lucide-react";

type CategoryFields = {
  category1Id: string | null;
  category1Name: string;
  category1Depth?: number | null;

  categoryPathIds?: string[];
  categoryPathNames?: string[];
};

type CategoryOption = {
  id: string;
  name: string;
  hasChildren: boolean;
};

type CategoryActionButton = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  disabledTooltip?: string;
};

interface CategoryComboboxProps<T extends CategoryFields> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  openCategory1: boolean;
  setOpenCategory1: (open: boolean) => void;
  t: (key: string) => string;
  language: string;
  disabled?: boolean;
  labelText?: string;
  resetKey?: number;
  emptyStateAction?: CategoryActionButton;
  navigatorHintText?: string;
}

const encodeValue = (opt: CategoryOption) => `${opt.id}::${opt.name}`;
const decodeId = (value: string) => value.split("::")[0] || "";
const requiredAsteriskStyle = { color: "var(--primary)" } as const;

const renderRequiredLabel = (label: string) => {
  const starIndex = label.indexOf("*");
  if (starIndex === -1) return label;
  const before = label.slice(0, starIndex);
  const after = label.slice(starIndex + 1);
  return (
    <>
      {before}
      <span style={requiredAsteriskStyle}>*</span>
      {after}
    </>
  );
};

export function CategoryCombobox<T extends CategoryFields>({
  formData,
  setFormData,
  openCategory1,
  setOpenCategory1,
  t,
  language,
  disabled = false,
  labelText,
  resetKey,
  emptyStateAction,
  navigatorHintText,
}: CategoryComboboxProps<T>) {
  const CATEGORY_API_URL = "/api/public/categories";
  const CONTROL_W = 280;

  const mapOptions = (data: any): CategoryOption[] => {
    const raw = Array.isArray(data) ? data : [];
    return raw
      .map((r: any) => ({
        id: String(r.id),
        name: String(r.name ?? ""),
        hasChildren: Boolean(r.hasChildren),
      }))
      .filter((r) => r.id.length > 0 && r.name.length > 0);
  };

  const [c1Query, setC1Query] = useState("");
  const [c1Path, setC1Path] = useState<CategoryOption[]>([]);
  const [c1Options, setC1Options] = useState<CategoryOption[]>([]);
  const [c1Loading, setC1Loading] = useState(false);
  const [c1Failed, setC1Failed] = useState(false);

  useEffect(() => {
    if (!openCategory1) return;

    const ids = formData.categoryPathIds || [];
    const names = formData.categoryPathNames || [];

    const navigationIds = ids.length > 1 ? ids.slice(0, -1) : [];
    const navigationNames = names.length > 1 ? names.slice(0, -1) : [];

    if (navigationIds.length > 0) {
      const restoredPath: CategoryOption[] = navigationIds.map((id, index) => ({
        id,
        name: navigationNames[index] || "",
        hasChildren: true,
      }));
      setC1Path(restoredPath);
    } else {
      setC1Path([]);
    }

    setC1Query("");
  }, [openCategory1, formData.categoryPathIds, formData.categoryPathNames]);

  useEffect(() => {
    setC1Path([]);
    setC1Options([]);
    setC1Query("");
  }, [resetKey]);

  const c1ParentId = useMemo(() => {
    return c1Path.length === 0 ? null : c1Path[c1Path.length - 1].id;
  }, [c1Path]);

  const requestChildren = useCallback(
    async (parentId: string | null) => {
      const res = await axios.get(CATEGORY_API_URL, {
        params: {
          ...(parentId ? { parentId } : {}),
          lang: language,
        },
      });
      return mapOptions(res.data);
    },
    [language]
  );

  useEffect(() => {
    if (!openCategory1 || disabled) return;

    let cancelled = false;

    const load = async () => {
      setC1Loading(true);
      setC1Failed(false);
      try {
        const options = await requestChildren(c1ParentId);
        if (!cancelled) setC1Options(options);
      } catch (err: any) {
        if (!cancelled) {
          setC1Options([]);
          setC1Failed(err?.response?.status !== 404);
        }
      } finally {
        if (!cancelled) setC1Loading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [openCategory1, c1ParentId, requestChildren, disabled]);

  const finalizeCategory1 = useCallback(
    (path: CategoryOption[] | null) => {
      if (!path || path.length === 0) {
        setFormData((prev) => ({
          ...prev,
          category1Id: null,
          category1Name: "",
          category1Depth: null,
          categoryPathIds: [],
          categoryPathNames: [],
        }));
        return;
      }

      const last = path[path.length - 1];
      setFormData((prev) => ({
        ...prev,
        category1Id: last.id,
        category1Name: last.name,
        category1Depth: path.length - 1,
        categoryPathIds: path.map((p) => p.id),
        categoryPathNames: path.map((p) => p.name),
      }));
    },
    [setFormData]
  );

  const clearManualSelection = useCallback(() => {
    if (disabled) return;
    finalizeCategory1(null);
    setC1Path([]);
    setC1Query("");
  }, [disabled, finalizeCategory1]);

  const handleSelectInC1 = useCallback(
    (value: string) => {
      if (disabled) return;
      const id = decodeId(value);
      const selected = c1Options.find((o) => o.id === id);
      if (!selected) return;

      finalizeCategory1([...c1Path, selected]);
      setC1Query("");
      setOpenCategory1(false);
    },
    [disabled, c1Options, c1Path, finalizeCategory1, setOpenCategory1]
  );

  const handleDiveInC1 = useCallback(
    (opt: CategoryOption) => {
      if (disabled || !opt.hasChildren) return;
      setC1Path((prev) => [...prev, opt]);
      setC1Query("");
    },
    [disabled]
  );

  const handleNavigateRoot = useCallback(() => {
    if (disabled) return;
    setC1Path([]);
    setC1Query("");
  }, [disabled]);

  const handleNavigateBreadcrumb = useCallback(
    (index: number) => {
      if (disabled) return;
      setC1Path((prev) => prev.slice(0, index + 1));
      setC1Query("");
    },
    [disabled]
  );

  const hasSelection = Boolean(formData.category1Id);
  const selectedPathNames = formData.categoryPathNames || [];
  const selectedPathLabel =
    selectedPathNames.length > 0
      ? selectedPathNames.join(" > ")
      : formData.category1Name || t("websiteSuggest.categorySelected");
  const showActionButton = hasSelection || Boolean(emptyStateAction);
  const isActionDisabled = disabled || Boolean(emptyStateAction?.disabled);
  const showActionTooltip =
    isActionDisabled && Boolean(emptyStateAction?.disabledTooltip);

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-secondary uppercase tracking-wide">
        {renderRequiredLabel(labelText ?? t("websiteSuggest.category1Label"))}
      </Label>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full">
        <div className="w-full sm:flex-none min-w-0" style={{ maxWidth: CONTROL_W }}>
          <Popover
            open={openCategory1}
            onOpenChange={(open) => {
              if (disabled) return;
              setOpenCategory1(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="h-10 w-full justify-between"
                disabled={disabled}
              >
                <span className="truncate">
                  {hasSelection
                    ? selectedPathLabel
                    : t("websiteSuggest.category1Placeholder")}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent style={{ width: CONTROL_W }} className="p-0">
              <div className="border-b px-3 py-2">
                <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap text-xs">
                  <button
                    type="button"
                    className="rounded px-1 py-0.5 hover:bg-accent"
                    onClick={handleNavigateRoot}
                  >
                    {t("websiteSuggest.categoryRootLabel")}
                  </button>
                  {c1Path.map((node, index) => (
                    <React.Fragment key={`${node.id}-${index}`}>
                      <ChevronRight className="h-3.5 w-3.5 opacity-60 shrink-0" />
                      <button
                        type="button"
                        className="rounded px-1 py-0.5 hover:bg-accent"
                        onClick={() => handleNavigateBreadcrumb(index)}
                      >
                        {node.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <Command>
                <CommandInput
                  placeholder={t("websiteSuggest.category1Placeholder")}
                  value={c1Query}
                  onValueChange={setC1Query}
                />
                <CommandList>
                  <CommandEmpty>
                    {c1Loading
                      ? t("websiteSuggest.loading")
                      : c1Failed
                        ? t("websiteSuggest.category1LoadFailed")
                        : c1Path.length > 0 && c1Options.length === 0
                          ? t("websiteSuggest.reachedLeaf")
                          : t("websiteSuggest.noCategory1Found")}
                  </CommandEmpty>
                  <CommandGroup>
                    {c1Options.map((opt) => (
                      <CommandItem
                        key={opt.id}
                        value={encodeValue(opt)}
                        onSelect={handleSelectInC1}
                      >
                        <div className="flex w-full items-center gap-2">
                          <span className="truncate">{opt.name}</span>
                          {opt.hasChildren ? (
                            <button
                              type="button"
                              className="ml-auto inline-flex h-8 w-10 items-center justify-center rounded border border-border/70 bg-muted/30 hover:bg-accent"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleDiveInC1(opt);
                              }}
                              title={t("websiteSuggest.categoryBrowseChildren")}
                            >
                              <ChevronRight className="h-4 w-4 opacity-70" />
                            </button>
                          ) : (
                            <span className="ml-auto inline-flex h-8 w-10 items-center justify-center text-sm leading-none opacity-70">
                              •
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {showActionButton && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {hasSelection && (
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full sm:w-auto px-4 shrink-0"
                onClick={clearManualSelection}
                disabled={disabled || !hasSelection}
              >
                {t("websiteSuggest.clearManualCategory")}
              </Button>
            )}
            {emptyStateAction && (
              showActionTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex w-full sm:w-auto" tabIndex={0}>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 w-full sm:w-auto px-4 shrink-0 justify-center gap-2"
                        onClick={emptyStateAction.onClick}
                        disabled={isActionDisabled}
                      >
                        {emptyStateAction.icon}
                        {emptyStateAction.label}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {emptyStateAction.disabledTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 w-full sm:w-auto px-4 shrink-0 justify-center gap-2"
                  onClick={emptyStateAction.onClick}
                  disabled={isActionDisabled}
                >
                  {emptyStateAction.icon}
                  {emptyStateAction.label}
                </Button>
              )
            )}
          </div>
        )}
      </div>

      {hasSelection && selectedPathNames.length > 0 && (
        <div className="text-sm text-secondary">
          <span className="font-semibold">{t("websiteSuggest.categoryPathPrefix")}</span>{" "}
          {selectedPathNames.join(" > ")}
        </div>
      )}
      {navigatorHintText && (
        <p className="text-xs text-secondary">{navigatorHintText}</p>
      )}
    </div>
  );
}

export default CategoryCombobox;
